<template>
  <button
    class="ai-banner"
    type="button"
    :disabled="loading"
    @click="handleClick"
  >
    <div class="ai-accent" />
    <div class="ai-glow" />

    <div
      v-if="loading"
      class="ai-loading-overlay"
    >
      <div class="ai-spinner" />
    </div>

    <div class="ai-content">
      <div class="ai-heading">
        <h3 class="ai-title">
          {{ title }}
        </h3>

        <span class="ai-badge">
          <span class="ai-badge-dot" />
          AI
        </span>
      </div>

      <p class="ai-subtitle">
        {{ subtitle }}
      </p>
    </div>

    <div
      v-if="image"
      class="ai-visual"
    >
      <div class="ai-visual-glow" />

      <img
        :src="image"
        alt=""
        draggable="false"
      />
    </div>
  </button>
</template>

<script setup>
const props = defineProps({
  title: {
    type: String,
    required: true
  },

  subtitle: {
    type: String,
    required: true
  },

  image: {
    type: String,
    default: ""
  },

  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  "click"
]);

function handleClick() {
  if (props.loading) return;

  emit("click");
}
</script>

<style scoped>
.ai-banner {
  position: relative;

  width: 330px;
  height: 122px;
  min-height: 122px;

  display: block;

  padding:
    17px
    82px
    17px
    18px;

  overflow: hidden;

  appearance: none;

  border:
    1px solid
    rgba(20, 24, 32, 0.08);

  border-radius: 22px;

  background:
    linear-gradient(
      145deg,
      #ffffff 0%,
      #fbfcfd 52%,
      #f8fafc 100%
    );

  color: inherit;
  text-align: left;

  cursor: pointer;

  box-shadow:
    0 8px 24px rgba(24, 32, 48, 0.055),
    0 2px 8px rgba(24, 32, 48, 0.025),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);

  transition:
    transform 180ms ease,
    border-color 180ms ease,
    box-shadow 180ms ease;
}

.ai-banner:hover:not(:disabled) {
  transform:
    translateY(-2px);

  border-color:
    rgba(40, 135, 247, 0.34);

  box-shadow:
    0 14px 32px rgba(24, 32, 48, 0.085),
    0 4px 10px rgba(24, 32, 48, 0.03),
    0 0 0 1px rgba(40, 135, 247, 0.035);
}

.ai-banner:active:not(:disabled) {
  transform:
    translateY(0)
    scale(0.992);
}

.ai-banner:focus-visible {
  outline:
    3px solid
    rgba(40, 135, 247, 0.18);

  outline-offset: 3px;
}

.ai-banner:disabled {
  cursor: default;
}

/* --------------------------------------------------------- */
/* ACCENT */
/* --------------------------------------------------------- */

.ai-accent {
  position: absolute;

  top: 22px;
  left: 0;

  width: 3px;
  height: 34px;

  border-radius:
    0
    999px
    999px
    0;

  background:
    linear-gradient(
      180deg,
      #2887f7 0%,
      #67aefc 100%
    );

  opacity: 0.9;

  box-shadow:
    0 0 12px
    rgba(40, 135, 247, 0.18);

  pointer-events: none;
}

/* --------------------------------------------------------- */
/* GLOW */
/* --------------------------------------------------------- */

.ai-glow {
  position: absolute;

  width: 180px;
  height: 180px;

  top: -82px;
  right: -74px;

  border-radius: 50%;

  background:
    radial-gradient(
      circle,
      rgba(40, 135, 247, 0.11) 0%,
      rgba(40, 135, 247, 0.035) 46%,
      transparent 72%
    );

  pointer-events: none;
}

/* --------------------------------------------------------- */
/* CONTENT */
/* --------------------------------------------------------- */

.ai-content {
  position: relative;
  z-index: 3;

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  min-width: 0;
}

.ai-heading {
  width: 100%;

  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 6px;
}

.ai-title {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 13px;
  font-weight: 700;
  line-height: 1.1;

  letter-spacing:
    -0.18px;

  color: #16181c;
}

.ai-badge {
  height: 18px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 4px;

  padding:
    0 7px;

  border:
    1px solid
    rgba(40, 135, 247, 0.08);

  border-radius: 999px;

  background:
    rgba(40, 135, 247, 0.075);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 9px;
  font-weight: 600;
  line-height: 1;

  letter-spacing:
    0.1px;

  color: #2887f7;

  white-space: nowrap;
}

.ai-badge-dot {
  width: 4px;
  height: 4px;

  flex-shrink: 0;

  border-radius: 50%;

  background: #2887f7;

  box-shadow:
    0 0 0 2px
    rgba(40, 135, 247, 0.1);
}

.ai-subtitle {
  width: 100%;
  max-width: 216px;

  margin:
    8px 0 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10.5px;
  font-weight: 400;
  line-height: 1.32;

  letter-spacing:
    -0.03px;

  color: #777b82;
}

/* --------------------------------------------------------- */
/* IMAGE */
/* --------------------------------------------------------- */

.ai-visual {
  position: absolute;
  z-index: 2;

  top: 5px;
  right: 1px;
  bottom: 5px;

  width: 80px;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  pointer-events: none;
}

.ai-visual-glow {
  position: absolute;

  width: 78px;
  height: 78px;

  top: 50%;
  left: 50%;

  transform:
    translate(-44%, -50%);

  border-radius: 50%;

  background:
    radial-gradient(
      circle,
      rgba(40, 135, 247, 0.12) 0%,
      rgba(40, 135, 247, 0.035) 52%,
      transparent 75%
    );

  filter: blur(2px);

  pointer-events: none;
}

.ai-visual img {
  position: relative;
  z-index: 2;

  display: block;

  width: 100%;
  height: 100%;

  object-fit: contain;
  object-position:
    right center;

  user-select: none;

  filter:
    drop-shadow(
      0 8px 12px
      rgba(0, 0, 0, 0.11)
    );

  transition:
    transform 220ms ease,
    filter 220ms ease;
}

.ai-banner:hover:not(:disabled)
.ai-visual img {
  transform:
    translateY(-1px)
    scale(1.025);

  filter:
    drop-shadow(
      0 10px 15px
      rgba(0, 0, 0, 0.15)
    );
}

/* --------------------------------------------------------- */
/* LOADING */
/* --------------------------------------------------------- */

.ai-loading-overlay {
  position: absolute;
  z-index: 20;

  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  border-radius: inherit;

  background:
    rgba(255, 255, 255, 0.78);

  backdrop-filter:
    blur(5px);
}

.ai-loading-overlay::before {
  content: "";

  position: absolute;

  width: 34%;
  height: 190%;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(40, 135, 247, 0.085),
      transparent
    );

  transform:
    rotate(14deg);

  animation:
    shimmer
    1.3s
    linear
    infinite;
}

.ai-spinner {
  position: relative;
  z-index: 2;

  width: 23px;
  height: 23px;

  border:
    2px solid
    rgba(25, 28, 35, 0.1);

  border-top-color:
    #2887f7;

  border-radius: 50%;

  animation:
    spin
    0.7s
    linear
    infinite;
}

/* --------------------------------------------------------- */
/* DARK */
/* --------------------------------------------------------- */

html[data-theme="dark"] .ai-banner {
  background:
    linear-gradient(
      145deg,
      #292b30 0%,
      #25272c 52%,
      #23252a 100%
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0 10px 26px
    rgba(0, 0, 0, 0.24),

    inset 0 1px 0
    rgba(255, 255, 255, 0.035);
}

html[data-theme="dark"]
.ai-banner:hover:not(:disabled) {
  border-color:
    rgba(40, 135, 247, 0.38);

  box-shadow:
    0 15px 34px
    rgba(0, 0, 0, 0.3),

    0 0 0 1px
    rgba(40, 135, 247, 0.045);
}

html[data-theme="dark"]
.ai-title {
  color: #f6f7f8;
}

html[data-theme="dark"]
.ai-subtitle {
  color:
    rgba(255, 255, 255, 0.58);
}

html[data-theme="dark"]
.ai-badge {
  border-color:
    rgba(105, 174, 252, 0.08);

  background:
    rgba(40, 135, 247, 0.12);

  color: #72b5ff;
}

html[data-theme="dark"]
.ai-badge-dot {
  background: #72b5ff;

  box-shadow:
    0 0 0 2px
    rgba(40, 135, 247, 0.12);
}

html[data-theme="dark"]
.ai-accent {
  background:
    linear-gradient(
      180deg,
      #4196fa 0%,
      #75b8ff 100%
    );

  box-shadow:
    0 0 14px
    rgba(40, 135, 247, 0.23);
}

html[data-theme="dark"]
.ai-glow {
  background:
    radial-gradient(
      circle,
      rgba(40, 135, 247, 0.15) 0%,
      rgba(40, 135, 247, 0.045) 45%,
      transparent 72%
    );
}

html[data-theme="dark"]
.ai-visual-glow {
  background:
    radial-gradient(
      circle,
      rgba(40, 135, 247, 0.14) 0%,
      rgba(40, 135, 247, 0.04) 52%,
      transparent 75%
    );
}

html[data-theme="dark"]
.ai-visual img {
  filter:
    brightness(1.04)
    drop-shadow(
      0 9px 14px
      rgba(0, 0, 0, 0.28)
    );
}

html[data-theme="dark"]
.ai-banner:hover:not(:disabled)
.ai-visual img {
  filter:
    brightness(1.06)
    drop-shadow(
      0 11px 17px
      rgba(0, 0, 0, 0.34)
    );
}

html[data-theme="dark"]
.ai-loading-overlay {
  background:
    rgba(27, 28, 32, 0.8);
}

html[data-theme="dark"]
.ai-loading-overlay::before {
  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.07),
      transparent
    );
}

html[data-theme="dark"]
.ai-spinner {
  border-color:
    rgba(255, 255, 255, 0.16);

  border-top-color:
    #2887f7;
}

/* --------------------------------------------------------- */
/* ANIMATIONS */
/* --------------------------------------------------------- */

@keyframes spin {
  to {
    transform:
      rotate(360deg);
  }
}

@keyframes shimmer {
  from {
    transform:
      translateX(-300%)
      rotate(14deg);
  }

  to {
    transform:
      translateX(400%)
      rotate(14deg);
  }
}

/* --------------------------------------------------------- */
/* ADAPTIVE */
/* --------------------------------------------------------- */

@media (max-width: 1300px) {
  .ai-banner {
    width: 310px;
    height: 116px;
    min-height: 116px;

    padding:
      15px
      76px
      15px
      16px;

    border-radius: 20px;
  }

  .ai-title {
    font-size: 12.5px;
  }

  .ai-subtitle {
    max-width: 202px;

    margin-top: 7px;

    font-size: 10px;
  }

  .ai-badge {
    height: 17px;

    padding:
      0 6px;

    font-size: 8.5px;
  }

  .ai-visual {
    width: 76px;
  }
}

@media (max-width: 430px) {
  .ai-banner {
    width: 100%;
    max-width: 100%;

    height: 116px;
    min-height: 116px;
  }
}
</style>